// Host-independent reef rules shared by room hosts and offline shared-screen play.
export const REEF_WIDTH = 1100, REEF_HEIGHT = 720;
export const CORAL_SAVE_KEY = "party-stack-coral-chorus-v1";
export const CORAL_FORMS = ["antlers", "tendrils", "fan", "plates", "cluster", "tubes"] as const;
export type CoralForm = typeof CORAL_FORMS[number];
export const CORAL_COLORS = [
  { name: "Guava", hex: "#ff837e" }, { name: "Rose", hex: "#ed80b4" },
  { name: "Lilac", hex: "#b9a0fa" }, { name: "Lagoon", hex: "#58d8d2" },
  { name: "Lime", hex: "#b7de84" }, { name: "Honey", hex: "#f9ce79" },
  { name: "Tangerine", hex: "#f6a16f" }, { name: "Bluebell", hex: "#7aaef2" },
] as const;
export interface ReefPoint { x: number; y: number }
export interface CoralIntent extends ReefPoint { form: CoralForm; color: string; size: number; rotation: number }
export interface CoralPiece extends CoralIntent { player: number; id: number }
export interface ReefState {
  version: 1; seed: number; players: string[]; selectedPlayer: number; pieces: CoralPiece[];
  title: string; admire: boolean; nextId: number;
}
export interface Attachment extends ReefPoint { piece: number | null }
export const BASE_ATTACHMENTS: Attachment[] = Array.from({ length: 13 }, (_, i) => ({
  x: 355 + i * 32.5, y: 565 - Math.sin(i / 12 * Math.PI) * 28, piece: null,
}));
// Matching attachment points follow the visible branches, rims and stems in the renderer.
export const FORM_POINTS: Record<CoralForm, ReefPoint[]> = {
  antlers: [[0, 0], [0, -.38], [-.26, -.56], [.3, -.64], [-.42, -.92], [-.08, -1], [.43, -.97], [.08, -.85]].map(([x, y]) => ({ x, y })),
  tendrils: [[0, 0], [-.2, -.4], [.23, -.45], [-.38, -.75], [.4, -.83], [0, -.94]].map(([x, y]) => ({ x, y })),
  fan: [[0, 0], [0, -.35], [-.52, -.57], [.52, -.57], [-.38, -.83], [.38, -.83], [0, -1]].map(([x, y]) => ({ x, y })),
  plates: [[0, 0], [-.4, -.25], [.4, -.25], [-.32, -.52], [.32, -.52], [-.22, -.77], [.22, -.77], [0, -.9]].map(([x, y]) => ({ x, y })),
  cluster: [[0, 0], [-.37, -.26], [.37, -.26], [-.25, -.6], [.25, -.6], [0, -.8]].map(([x, y]) => ({ x, y })),
  tubes: [[0, 0], [-.28, -.5], [-.3, -.77], [0, -1], [.29, -.62]].map(([x, y]) => ({ x, y })),
};
export function cleanReefText(value: unknown, max: number, fallback: string) {
  return typeof value === "string" ? value.replace(/[\u0000-\u001f\u007f]/g, "").trim().slice(0, max) || fallback : fallback;
}
export function reefPlayers(names: unknown): string[] {
  const list = Array.isArray(names) ? names.slice(0, 6) : [];
  while (list.length < 2) list.push("");
  return list.map((name, i) => cleanReefText(name, 24, `Player ${i + 1}`));
}
export function newReef(names: unknown = [], seed = 241107): ReefState {
  return { version: 1, seed: seed >>> 0, players: reefPlayers(names), selectedPlayer: 0, pieces: [], title: "Our little ocean", admire: false, nextId: 1 };
}
export function transformCoral(piece: CoralIntent, point: ReefPoint): ReefPoint {
  const angle = piece.rotation * Math.PI / 180;
  return { x: piece.x + piece.size * (point.x * Math.cos(angle) - point.y * Math.sin(angle)), y: piece.y + piece.size * (point.x * Math.sin(angle) + point.y * Math.cos(angle)) };
}
export function reefAttachments(pieces: readonly CoralPiece[]): Attachment[] {
  return [...BASE_ATTACHMENTS, ...pieces.flatMap(p => FORM_POINTS[p.form].map(point => ({ ...transformCoral(p, point), piece: p.id })))];
}
export function nearestAttachment(pieces: readonly CoralPiece[], point: ReefPoint) {
  let nearest = BASE_ATTACHMENTS[0], distance = Infinity;
  for (const attachment of reefAttachments(pieces)) {
    const d = Math.hypot(point.x - attachment.x, point.y - attachment.y);
    if (d < distance) { nearest = attachment; distance = d; }
  }
  return { point: nearest, distance };
}
export function snapCoral(pieces: readonly CoralPiece[], intent: CoralIntent): CoralIntent {
  const nearest = nearestAttachment(pieces, intent);
  return nearest.distance <= 38 ? { ...intent, x: nearest.point.x, y: nearest.point.y } : intent;
}
export function validCoralIntent(value: unknown): value is CoralIntent {
  if (!value || typeof value !== "object") return false;
  const p = value as CoralIntent;
  return CORAL_FORMS.includes(p.form) && CORAL_COLORS.some(c => c.hex === p.color)
    && [p.x, p.y, p.size, p.rotation].every(Number.isFinite)
    && p.size >= 55 && p.size <= 180 && p.rotation >= -180 && p.rotation <= 180
    && p.x >= 65 && p.x <= 1035 && p.y >= 90 && p.y <= 612;
}
export function canPlaceCoral(state: ReefState, intent: unknown): intent is CoralIntent {
  if (state.admire || !validCoralIntent(intent)) return false;
  // A forgiving connection radius and unrestricted overlaps keep construction playful.
  return nearestAttachment(state.pieces, intent).distance <= 38;
}
export function placeCoral(state: ReefState, intent: CoralIntent, player = state.selectedPlayer): ReefState | null {
  if (!canPlaceCoral(state, intent) || !Number.isInteger(player) || player < 0 || player >= state.players.length) return null;
  const connected = snapCoral(state.pieces, intent);
  return { ...state, pieces: [...state.pieces, { ...connected, player, id: state.nextId }], nextId: state.nextId + 1 };
}
export function undoCoral(state: ReefState): ReefState {
  const last = state.pieces.at(-1);
  if (!last) return state;
  return { ...state, pieces: state.pieces.slice(0, -1), nextId: last.id, admire: false };
}
export function changeReefPlayers(state: ReefState, names: unknown): ReefState {
  const players = reefPlayers(names);
  return { ...state, players, selectedPlayer: Math.min(state.selectedPlayer, players.length - 1) };
}
const rgb = (hex: string) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
export function mixCoralColors(a: string, b: string, amount: number): string {
  const first = rgb(a), second = rgb(b);
  return "#" + first.map((c, i) => Math.round(c + (second[i] - c) * amount).toString(16).padStart(2, "0")).join("");
}
export function coralHighlight(piece: CoralIntent, others: readonly CoralIntent[]): string {
  const center = transformCoral(piece, { x: 0, y: -.45 });
  const sum = [0, 0, 0]; let total = 0;
  for (const other of others) {
    if (other === piece) continue;
    const otherCenter = transformCoral(other, { x: 0, y: -.45 });
    const radius = (piece.size + other.size) * .55 + 35;
    const weight = Math.max(0, 1 - Math.hypot(center.x - otherCenter.x, center.y - otherCenter.y) / radius);
    if (weight === 0) continue;
    rgb(other.color).forEach((c, i) => { sum[i] += c * weight; }); total += weight;
  }
  if (!total) return mixCoralColors(piece.color, "#ffffff", .22);
  const neighbor = "#" + sum.map(c => Math.round(c / total).toString(16).padStart(2, "0")).join("");
  return mixCoralColors(mixCoralColors(piece.color, neighbor, .62), "#ffffff", .16);
}
export function reefFishCount(pieces: readonly CoralIntent[]): number {
  if (pieces.length < 3) return 0;
  const variety = new Set(pieces.map(p => p.form)).size + new Set(pieces.map(p => p.color)).size;
  return Math.min(12, Math.floor(pieces.length / 3) + Math.floor(variety / 4));
}
export function reefScenePoint(rect: { left: number; top: number; width: number; height: number }, x: number, y: number): ReefPoint {
  const scale = Math.min(rect.width / REEF_WIDTH, rect.height / REEF_HEIGHT);
  return { x: (x - rect.left - (rect.width - REEF_WIDTH * scale) / 2) / scale, y: (y - rect.top - (rect.height - REEF_HEIGHT * scale) / 2) / scale };
}
export function parseReefSave(raw: string | null): ReefState {
  const fallback = newReef();
  try {
    const saved = JSON.parse(raw || "null");
    if (!saved || saved.version !== 1 || !Array.isArray(saved.pieces)) return fallback;
    let state = newReef(saved.players, Number.isInteger(saved.seed) ? saved.seed : fallback.seed);
    // Revalidate connections in order. Never trust saved coordinates or prototype data.
    for (const p of saved.pieces) {
      if (!validCoralIntent(p)) continue;
      const next = placeCoral(state, p);
      if (!next) continue;
      const piece = next.pieces.at(-1)!;
      const player = (p as Partial<CoralPiece>).player;
      piece.player = Number.isInteger(player) && player! >= 0 && player! < 6 ? player! : 0;
      state = next;
    }
    // Older saves used a rotating turn. Keep that name selected for credit only.
    const selected = saved.selectedPlayer ?? saved.turn;
    return { ...state, selectedPlayer: Number.isInteger(selected) && selected >= 0 && selected < state.players.length ? selected : 0,
      title: cleanReefText(saved.title, 60, fallback.title), admire: saved.admire === true };
  } catch { return fallback; }
}
